import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsLoadingButton } from '../basic/QcsLoadingButton';
import { QcsTooltip } from '../basic/QcsTooltip';
import { Resource } from '@api/resources';

interface StyledLoadingButtonProps {
  ignoreMarginLeft?: boolean;
}

export const StyledLoadingButton = styled(QcsLoadingButton)<StyledLoadingButtonProps>(({ theme, ignoreMarginLeft }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'fit-content',
    marginLeft: ignoreMarginLeft ? '0' : 'auto',
  },
}));

//A disabled button fires no mouse events, so the tooltip hangs on this wrapper - which takes over the button's placement.
const DisabledHintAnchor = styled.span<StyledLoadingButtonProps>(({ theme, ignoreMarginLeft }) => ({
  display: 'inline-flex',
  width: '100%',
  '& > *': { width: '100%' },
  [theme.breakpoints.up('sm')]: {
    width: 'fit-content',
    marginLeft: ignoreMarginLeft ? '0' : 'auto',
  },
}));

interface Props {
  className?: string;
  disabled?: boolean;
  text?: string | null;
  ignoreMarginLeft?: boolean;
  onClick?: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  resourceLoading?: Resource | Resource[];
  /** Disables the button and tells why on hover, so it never looks clickable while doing nothing. */
  disabledHint?: string | null;
}

export const SubmitButton: FC<Props> = ({
  className,
  disabled,
  text,
  ignoreMarginLeft,
  onClick,
  resourceLoading,
  variant = 'contained',
  disabledHint,
}) => {
  const { isSubmitting } = useFormikContext();
  const { t } = useTranslation();

  const button = (
    <StyledLoadingButton
      className={className}
      variant={variant}
      type="submit"
      disabled={disabled || !!disabledHint || isSubmitting}
      loading={isSubmitting}
      ignoreMarginLeft={ignoreMarginLeft}
      onClick={onClick}
      resourceLoading={resourceLoading}
    >
      {t(text ?? 'common.save')}
    </StyledLoadingButton>
  );

  if (!disabledHint) return button;

  return (
    <QcsTooltip title={disabledHint}>
      <DisabledHintAnchor ignoreMarginLeft={ignoreMarginLeft}>{button}</DisabledHintAnchor>
    </QcsTooltip>
  );
};
